import React, { useState, useEffect } from "react";
import { requirementService } from "../../services/ApiService";
import { 
  FiUsers, 
  FiLayers, 
  FiCheckCircle, 
  FiAlertCircle, 
  FiLoader,
  FiSearch,
  FiSave,
  FiFolder
} from "react-icons/fi";
import "./ProjectMapping.css";

const ProjectMapping = () => {
  // Core Data States
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  
  // Form Selection States
  const [targetUserId, setTargetUserId] = useState("");
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  
  // UI Management States
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Helper check to see if a selected profile is Milind (Admin)
// Immutable Security Filter for Site Administrator
  const isTargetUserAdmin = () => {
    const activeUser = users.find(u => u.id === Number(targetUserId));
    if (!activeUser) return false;
    
    // Hardened check against explicit structural ID and admin corporate email address
    return (
      activeUser.email === "milind@tapasyatech.in" ||
      activeUser.is_superuser || 
      activeUser.role === 'admin'
    );
  };

  // Load users and projects from the API service
  const loadWorkspaceMatrix = async () => {
    setError(null);
    try {
      const userRes = await requirementService.getAllUsers();
      const projectRes = await requirementService.getAllMasterProjects();

      if (userRes.status && projectRes.status) {
        setUsers(userRes.data);
        setProjects(projectRes.data);
      } else {
        setError("Invalid response data format from server.");
      }
    } catch (err) {
      console.error("Error loading data:", err);
      setError(err.message || "Failed to fetch users or projects.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWorkspaceMatrix();
  }, []);

  // Sync checkboxes whenever the selected user changes
  useEffect(() => {
    if (!targetUserId) {
      setSelectedProjectIds([]);
      return;
    }
    
    // Find all project IDs where this user is assigned
    const currentlyAssigned = projects
      .filter(project => project.assigned_users.includes(Number(targetUserId)))
      .map(project => project.id);
      
    setSelectedProjectIds(currentlyAssigned);
  }, [targetUserId, projects]);

  const triggerToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "success" }), 4000);
  };

  // Handle checking/unchecking a project
  const handleCheckboxToggle = (projectId) => {
    const isProjectCurrentlyAssigned = projects
      .find(p => p.id === projectId)
      ?.assigned_users.includes(Number(targetUserId));

    // Admin Lockdown Rule: If they are an admin and it's already assigned, do not let them uncheck it.
    if (isTargetUserAdmin() && isProjectCurrentlyAssigned) {
      triggerToast("Administrative scope lock active. Baseline paths cannot be removed.", "error");
      return;
    }

    setSelectedProjectIds(prev => 
      prev.includes(projectId) 
        ? prev.filter(id => id !== projectId) 
        : [...prev, projectId]
    );
  };

  // Compare original state vs checkbox state and send updates to server
  const handleSaveChanges = async (e) => {
    e.preventDefault();
    if (!targetUserId) return;

    setSaving(true);
    const userIdNum = Number(targetUserId);
    
    // Find projects the user originally had assigned
    const originalAssignments = projects
      .filter(p => p.assigned_users.includes(userIdNum))
      .map(p => p.id);

    // Calculate delta maps
    const assignmentsToAdd = selectedProjectIds.filter(id => !originalAssignments.includes(id));
    const assignmentsToRemove = originalAssignments.filter(id => !selectedProjectIds.includes(id));

    try {
      // Run assignments
      for (const id of assignmentsToAdd) {
        await requirementService.assignProject(userIdNum, id);
      }
      // Run unassignments
      for (const id of assignmentsToRemove) {
        await requirementService.unassignProject(userIdNum, id);
      }

      triggerToast("Project assignments updated successfully.", "success");
      
      // Refresh database records to update the screen state
      const refreshedProjects = await requirementService.getAllMasterProjects();
      if (refreshedProjects.status) {
        setProjects(refreshedProjects.data);
      }
    } catch (err) {
      triggerToast(err.message || "Failed to update assignments.", "error");
    } finally {
      setSaving(false);
    }
  };

  // Filter users table rows via search input box
  const filteredUsers = users.filter(user => 
    user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="tech-light-loading">
        <FiLoader className="spinner-icon-light" />
        <span>Loading Project Mapping Dashboard...</span>
      </div>
    );
  }

  return (
    <div className="light-mapping-viewport">
      {/* Toast Notification Container */}
      {toast.show && (
        <div className={`light-toast ${toast.type}`}>
          <FiCheckCircle /> <span>{toast.message}</span>
        </div>
      )}

      {/* Dashboard Header Bar */}
      <div className="light-viewport-header">
        <div>
          <h2>Project Mapping Dashboard</h2>
          <p className="subtitle">Assign projects to users and manage system access.</p>
        </div>
        {error && <div className="light-error-banner"><FiAlertCircle /> {error}</div>}
      </div>

      <div className="light-workspace-layout">
        
        {/* LEFT COMPONENT: Assignment Control Form */}
        <div className="light-panel action-form-panel">
          <div className="panel-heading-bar">
            <h3><FiLayers /> Assign Projects</h3>
          </div>
          
          <form onSubmit={handleSaveChanges} className="assignment-form">
            <div className="form-group-block">
              <label htmlFor="user-select">Select User</label>
              <select
                id="user-select"
                value={targetUserId}
                onChange={(e) => setTargetUserId(e.target.value)}
                className="tech-light-select"
                required
              >
                <option value="">-- Choose User --</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.username} ({u.email})</option>
                ))}
              </select>
            </div>

            <div className="form-group-block project-checklist-wrapper">
              <label>Select Projects to Assign</label>
              {!targetUserId ? (
                <div className="form-fallback-notice">
                  <FiUsers /> <span>Please select a user to configure their project assignments.</span>
                </div>
              ) : (
                <div className="project-checkbox-scroller">
                  {projects.map(project => {
                    const checked = selectedProjectIds.includes(project.id);
                    const isOriginallyAssigned = project.assigned_users.includes(Number(targetUserId));
                    
                    // Disable checkbox ONLY if it's an admin user trying to remove an already assigned project
                    const isCheckboxDisabled = isTargetUserAdmin() && isOriginallyAssigned;

                    return (
                      <label 
                        key={project.id} 
                        className={`checkbox-item-row ${checked ? "row-checked" : ""} ${isCheckboxDisabled ? "row-disabled-admin" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={isCheckboxDisabled}
                          onChange={() => handleCheckboxToggle(project.id)}
                        />
                        <div className="checkbox-custom-label">
                          <span className="project-node-title">
                            {project.name} {isCheckboxDisabled && <span className="admin-lock-tag">Locked</span>}
                          </span>
                          <span className="project-node-desc">
                            {project.description || "No description provided for this project."}
                          </span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <button
              type="submit"
              className="tech-light-btn-submit"
              disabled={saving || !targetUserId}
            >
              {saving ? (
                <FiLoader className="spinner-icon-light speed-fast" />
              ) : (
                <>
                  <FiSave /> <span>Save Assignments</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* RIGHT COMPONENT: Global Assignments Directory Table */}
        <div className="light-panel monitoring-directory-panel">
          <div className="panel-heading-bar split-directory-header">
            <h3><FiUsers /> User Assignment List</h3>
            
            <div className="directory-search-input-box">
              <FiSearch className="search-box-icon" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="directory-table-responsive-box">
            <table className="directory-data-table">
              <thead>
                <tr>
                  <th>User Details</th>
                  <th>Assigned Projects</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => {
                  const boundProjects = projects.filter(p => p.assigned_users.includes(user.id));
                  const isUserAdmin = user.is_superuser || user.role === 'admin' || user.username?.toLowerCase().includes("milind");
                  
                  return (
                    <tr key={user.id} className={Number(targetUserId) === user.id ? "row-highlight-active" : ""}>
                      <td className="profile-cell-block">
                        <span className="profile-cell-title">
                          {user.username} {isUserAdmin && <span className="admin-badge">Admin</span>}
                        </span>
                        <span className="profile-cell-subtitle">{user.email}</span>
                      </td>
                      <td>
                        <div className="pill-flex-wrapbox">
                          {boundProjects.map(bp => (
                            <span key={bp.id} className="environment-scope-pilltag">
                              <FiFolder className="pill-folder-icon" /> {bp.name}
                            </span>
                          ))}
                          {boundProjects.length === 0 && (
                            <span className="no-access-indicator">No projects assigned.</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan="2" className="table-empty-notice">
                      No users match the search keyword "{searchQuery}".
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ProjectMapping;